import { MessageCircle, Check, Sparkle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";
import LeadQuiz from "@/components/LeadQuiz";
import AzulejoStrip from "@/components/AzulejoStrip";
import { usePromoCountdown } from "@/components/layout/PromoBanner";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";

const pad = (n) => String(n).padStart(2, "0");

const Pricing = () => {
  const { t, lang } = useLanguage();
  const pp = t.pricingPage;
  // TIME-LIMITED: September promo block — hides automatically after 2026-09-30
  const { active, days, hours, mins, secs } = usePromoCountdown();

  const PackageCard = ({ name, desc, includes, price, note, featured, testid }) => (
    <div
      className={`rounded-2xl p-8 md:p-10 h-full flex flex-col ${
        featured ? "bg-terracotta text-ink" : "bg-white border border-stone"
      }`}
      data-testid={testid}
    >
      {featured && (
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-ink/80 mb-4">{pp.bestValue}</span>
      )}
      <h3 className="font-serif text-3xl mb-4">{name}</h3>
      <p className={`${featured ? "text-ink/85" : "text-ink/70"} leading-relaxed mb-7`}>{desc}</p>
      <ul className="space-y-3 mb-8">
        {includes.map((inc, i) => (
          <li key={i} className="flex items-start gap-3">
            <Check size={18} className={`${featured ? "text-ink" : "text-olive"} mt-0.5 shrink-0`} aria-hidden />
            <span className={featured ? "text-ink/90" : "text-ink/80"}>{inc}</span>
          </li>
        ))}
      </ul>
      <div className="mt-auto flex items-end justify-between gap-4">
        <div>
          <span className="font-serif text-4xl md:text-5xl">{price}</span>
          <p className={`font-mono text-xs mt-2 ${featured ? "text-ink/70" : "text-stone-fg"}`}>{note}</p>
        </div>
        <a
          href={CONTACT.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testid}-book`}
          className={`inline-flex items-center gap-2 font-medium px-5 py-2.5 rounded-full transition-all hover:-translate-y-0.5 ${
            featured ? "bg-white text-bronze" : "bg-ink text-sand hover:bg-terracotta"
          }`}
        >
          {pp.bookCta}
        </a>
      </div>
    </div>
  );

  return (
    <div data-testid="pricing-page">
      <Seo
        title={lang === "es" ? "Precios — Marina Knows" : "Pricing — Marina Knows"}
        description={lang === "es"
          ? "Precios claros y fijos: NIE 120 €, TIE 180 €, empadronamiento 60 €, traducción desde 35 €. Pack Primeros 90 días por 350 €. 50% de descuento en septiembre."
          : "Clear, fixed prices: NIE €120, TIE €180, empadronamiento €60, translation from €35. First 90 Days package €350. 50% off September bookings."}
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-14">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-olive mb-4">{pp.overline}</p>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tighter text-ink mb-6 max-w-3xl" data-testid="pricing-title">{pp.title}</h1>
        <p className="text-lg text-ink/70 max-w-2xl">{pp.sub}</p>
      </section>

      {/* TIME-LIMITED PROMO: September 2026 — remove with PromoBanner after the month ends */}
      {active && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16" data-testid="pricing-promo">
          <Reveal>
            <div className="bg-terracotta text-ink rounded-2xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <p className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/80 mb-4">
                  <Sparkle size={14} aria-hidden /> {pp.promoTitle}
                </p>
                <p className="font-serif text-3xl md:text-4xl leading-tight mb-4">{pp.promoText}</p>
                <p className="text-ink/80">{pp.promoValid}</p>
              </div>
              <div className="md:col-span-4 text-center">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-ink/70 mb-3">{t.promo.endsIn}</p>
                <p className="font-serif text-5xl md:text-6xl tabular-nums" data-testid="pricing-countdown">
                  {days}
                  <span className="text-2xl align-top">{t.promo.days}</span> {pad(hours)}
                  <span className="text-2xl align-top">{t.promo.hours}</span> {pad(mins)}
                  <span className="text-2xl align-top">{t.promo.mins}</span>
                </p>
                <a
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pricing-promo-cta"
                  className="mt-5 inline-flex items-center gap-2 bg-white text-bronze font-medium px-6 py-3 rounded-full transition-all hover:-translate-y-0.5"
                >
                  <MessageCircle size={16} aria-hidden />
                  {pp.cta}
                </a>
              </div>
            </div>
          </Reveal>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20" data-testid="packages-section">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl text-ink mb-10">{pp.packagesTitle}</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal>
            <PackageCard featured testid="pricing-pkg-first90" name={pp.pkg1Name} desc={pp.pkg1Desc}
              includes={pp.pkg1Includes} price={pp.pkg1Price} note={pp.pkg1Note} />
          </Reveal>
          <Reveal delay={0.1}>
            <PackageCard testid="pricing-pkg-retainer" name={pp.pkg2Name} desc={pp.pkg2Desc}
              includes={pp.pkg2Includes} price={pp.pkg2Price} note={pp.pkg2Note} />
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-28" data-testid="alacarte-section">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl text-ink mb-10">{pp.alaCarteTitle}</h2>
        </Reveal>
        <div className="space-y-10">
          {t.servicesPage.groups.map((group, gi) => (
            <Reveal key={gi} delay={gi * 0.03}>
              <div className="bg-white border border-stone rounded-2xl overflow-hidden" data-testid={`price-table-${gi}`}>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-olive px-7 pt-6 pb-4">{group.name}</p>
                <ul>
                  {group.items.map((item, ii) => (
                    <li
                      key={ii}
                      className="flex justify-between items-center gap-4 px-7 py-4 border-t border-stone/70 hover:bg-sand/60 transition-colors"
                      data-testid={`price-row-${gi}-${ii}`}
                    >
                      <span className="text-ink/85">{item.name}</span>
                      <span className="font-mono text-bronze font-medium whitespace-nowrap">
                        {item.price}
                        {item.unit && <span className="text-stone-fg text-xs ml-2 hidden sm:inline">{t.servicesPage[item.unit]}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <AzulejoStrip />

      {/* LEAD QUIZ */}
      <section className="bg-cream border-t border-stone" data-testid="quiz-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28">
          <LeadQuiz />
        </div>
      </section>
    </div>
  );
};

export default Pricing;
